import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, ArrowRight } from "lucide-react";

const PartnerRequestDialog = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    vat: "",
    contact_name: "",
    email: "",
    phone: "",
    monthly_volume_kg: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.vat || !form.contact_name || !form.email) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    const cleanPhone = (form.phone || "").replace(/[\s.-]/g, "");
    if (cleanPhone.length < 7) {
      toast.error("Il numero di telefono è obbligatorio");
      return;
    }
    const cleanVat = (form.vat || "").replace(/[\s-]/g, "").toUpperCase();
    const isPiva = /^\d{11}$/.test(cleanVat);
    const isCf = /^[A-Z0-9]{16}$/.test(cleanVat);
    if (!(isPiva || isCf)) {
      toast.error("P.IVA non valida", { description: "Inserisci 11 cifre (P.IVA) oppure 16 caratteri (Codice Fiscale)." });
      return;
    }
    setSending(true);
    try {
      await api.post("/partner-requests", {
        ...form,
        monthly_volume_kg: form.monthly_volume_kg ? parseFloat(form.monthly_volume_kg) : null,
      });
      setDone(true);
      toast.success("Richiesta inviata!", { description: "Ti risponderemo entro 24 ore." });
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || "Riprova più tardi." });
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm({ company_name: "", vat: "", contact_name: "", email: "", phone: "", monthly_volume_kg: "", message: "" });
    setDone(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTimeout(reset, 300); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="partner-request-dialog">
        {done ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 grid place-items-center mx-auto mb-4">
              <Sparkles className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="font-heading text-2xl font-medium text-[#032B44]">Richiesta ricevuta</h3>
            <p className="text-sm text-slate-600 mt-2 max-w-sm mx-auto">
              Grazie! Il nostro team ti contatterà entro 24 ore per attivare il tuo listino personalizzato.
            </p>
            <Button onClick={() => setOpen(false)} className="mt-6 bg-[#032B44] hover:bg-[#00A3FF] text-white" data-testid="partner-close-button">Chiudi</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-[#032B44]">
                Richiedi listino partner
              </DialogTitle>
              <p className="text-sm text-slate-600 mt-1">
                Bar, ristoranti, hotel e catering: ricevi prezzi dedicati in base ai volumi.
              </p>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Ragione sociale *" testId="prq-company">
                  <Input value={form.company_name} onChange={upd("company_name")} required data-testid="prq-company-input" />
                </Field>
                <Field label="P.IVA * (11 cifre) o CF (16 caratteri)" testId="prq-vat">
                  <Input value={form.vat} onChange={upd("vat")} required data-testid="prq-vat-input" />
                </Field>
                <Field label="Referente *" testId="prq-contact">
                  <Input value={form.contact_name} onChange={upd("contact_name")} required data-testid="prq-contact-input" />
                </Field>
                <Field label="Email *" testId="prq-email">
                  <Input type="email" value={form.email} onChange={upd("email")} required data-testid="prq-email-input" />
                </Field>
                <Field label="Telefono *" testId="prq-phone">
                  <Input type="tel" value={form.phone} onChange={upd("phone")} required data-testid="prq-phone-input" />
                </Field>
                <Field label="Volume mensile stimato (kg)" testId="prq-volume">
                  <Input type="number" min="0" value={form.monthly_volume_kg} onChange={upd("monthly_volume_kg")} data-testid="prq-volume-input" />
                </Field>
              </div>
              <Field label="Note (prodotti di interesse, frequenza...)" testId="prq-message">
                <Textarea value={form.message} onChange={upd("message")} rows={3} data-testid="prq-message-input" />
              </Field>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-sky-200">Annulla</Button>
                <Button type="submit" disabled={sending} className="bg-[#032B44] hover:bg-[#00A3FF] text-white" data-testid="prq-submit-button">
                  {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio…</> : <>Invia richiesta <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs tracking-wider uppercase font-bold text-sky-600 block mb-1.5">{label}</span>
    {children}
  </label>
);

export default PartnerRequestDialog;
